/* @flow */
import type {Node} from 'react'
import React, {lazy, useState, Suspense, useRef, useCallback, useEffect} from 'react'
import classNames from 'classnames'
import {type FakeOnKeyDownEvent, type EditorSettings} from 'components/ui/CKEditor/types'
import LoadingDots from 'components/ui/LoadingDots'
import Markdown from 'components/ui/Markdown'
import util from 'utils/MiscUtils'
import focusWhenModalShown from 'utils/focusWhenModalShown'
import styles from './index.scss'

type Point = { x: number, y: number }

const SharedLazyWYSIWYG = lazy(() => import(
  /* webpackChunkName: "WYSIWYGTextarea" */
  '../WYSIWYGTextarea'
))

export type CKAutocompleteItem = $ReadOnly<{
  link: string,
  name: string,
  id: string,
}>;

export type Props = $ReadOnly<{
  autofocus?: boolean,
  isPlainText?: boolean,
  editorSettings?: EditorSettings,
  fieldName: string,
  fieldValue: string,
  rows?: number,
  placeholder?: string,
  blurOnEnterKeystroke?: boolean,
  plaintextClasses?: string,
  loadOnFocus?: boolean, // if true, load editor only after user focuses/clicks
  previewText?: string, // text to show in the placeholder before loading (if loadOnFocus is true)
}>

function RailsWYSIWYG({
  autofocus = false,
  editorSettings,
  isPlainText = false,
  fieldName,
  fieldValue,
  rows = 5,
  placeholder = '',
  blurOnEnterKeystroke,
  plaintextClasses,
  loadOnFocus = false,
  previewText = '',
}: Props): null | Node {
  const [newValue, setNewValue] = useState(fieldValue || '')
  const inputRef = useRef<?HTMLTextAreaElement>(null)

  const [loaded, setLoaded] = useState(!loadOnFocus)
  const [autofocusOnLoad, setAutofocusOnLoad] = useState(false)
  const [focusPoint, setFocusPoint] = useState<?Point>(null)

  const handleChange = useCallback((e: any) => {
    const val = e.target.value
    setNewValue(val)
    if (inputRef.current)
      inputRef.current.dispatchEvent(new Event('change', {bubbles: true, cancelable: true}))
  }, [])

  const handleFocus = useCallback(() => {
    if (inputRef.current)
      inputRef.current.dispatchEvent(new Event('focus'))
    else
      util.warn('wrapperRef.current is null')
  }, [inputRef])

  const handleBlur = useCallback(() => {
    if (!inputRef.current)
      return

    const currentVal = inputRef.current.value ?? ''
    const initialVal = fieldValue || ''

    // don't trigger anything if value hasn't changed
    if (currentVal !== initialVal) {
      setTimeout(() => {
        const blurEvent = new FocusEvent('blur', {bubbles: true, cancelable: true})
        inputRef.current?.dispatchEvent(blurEvent)

        // Dispatch a change event to ensure state is updated
        const changeEvent = new Event('change', {bubbles: true, cancelable: true})
        inputRef.current?.dispatchEvent(changeEvent)
      }, 10)
    }
  }, [inputRef, fieldValue])

  const onKeyDown = (event: any | FakeOnKeyDownEvent) => {
    if (event.key !== 'Enter')
      return

    if (!event.shiftKey) {
      event.preventDefault()
      handleBlur()
    }
  }

  const ensureLoaded = useCallback((focusAfterLoad: boolean = false, point: ?Point = null) => {
    setLoaded(true)
    if (focusAfterLoad)
      setAutofocusOnLoad(true)
    if (point)
      setFocusPoint(point)
  }, [])

  useEffect(() => {
    if (!isPlainText || !autofocus)
      return undefined

    return focusWhenModalShown(inputRef.current, () => inputRef.current?.focus())
  }, [isPlainText, autofocus])

  useEffect(() => {
    if (!loadOnFocus)
      ensureLoaded(false)
  }, [loadOnFocus, ensureLoaded])

  // trigger loader and capture mouse coords when available
  const triggerLoadAndFocus = useCallback((e?: SyntheticMouseEvent<HTMLElement>) => {
    if (e && typeof e.clientX === 'number' && typeof e.clientY === 'number') {
      e.preventDefault()
      ensureLoaded(true, {x: e.clientX, y: e.clientY})
    } else {
      ensureLoaded(true)
    }
  }, [ensureLoaded])

  if (isPlainText) {
    const allPlaintextClasses = classNames(plaintextClasses, styles.plainText)

    return (
      <textarea
        className={allPlaintextClasses}
        name={fieldName}
        onChange={handleChange}
        onKeyDown={blurOnEnterKeystroke ? onKeyDown : undefined}
        placeholder={placeholder}
        ref={inputRef}
        rows={rows}
        value={newValue}
      />
    )
  }

  const containerClasses = classNames(styles.lazyPreviewContainer, editorSettings?.mode, {
    [styles.inlineEditor]: editorSettings?.mode === 'inline-edit',
    [styles.inlineEditorInTable]: editorSettings?.mode === 'inline-edit-table',
  })

  return (
    <div test-id={`RailsWYSIWYG-${fieldName}`} className={containerClasses}>
      {loadOnFocus && !loaded ? (
        <div
          className={`${styles.lazyPreview}`}
          role="button"
          tabIndex={0}
          onMouseDown={triggerLoadAndFocus}
          onFocus={triggerLoadAndFocus}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              triggerLoadAndFocus()
            }
          }}
        >
          <div className={`${styles.lazyPreviewInner}`}>
            {previewText && (
            <Markdown
              className={styles.lazyPreviewText}
              source={previewText}
              unwrapDisallowed
              disallowedTypes={['p']}
            />
            )}
          </div>
        </div>
      ) : (
        <Suspense fallback={<LoadingDots sm />}>
          {loaded ? (
            <SharedLazyWYSIWYG
              autofocus={autofocus || autofocusOnLoad}
              editorSettings={editorSettings}
              name={fieldName}
              label={fieldName}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onKeyDown={blurOnEnterKeystroke ? onKeyDown : undefined}
              rows={rows}
              value={fieldValue}
              placeholder={placeholder}
              tinyMarginBottom
              hideLabel
              initialFocusPoint={focusPoint || undefined}
            />
          ) : (
            <LoadingDots />
          )}
        </Suspense>
      )}
      <textarea
        className={styles.hidden}
        name={fieldName}
        readOnly
        value={newValue}
        ref={inputRef}
      />
    </div>
  )
}

export default RailsWYSIWYG
